import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, Upload } from 'lucide-react'
import type { Canon, Profile, Work } from '../../core/types.ts'
import { deriveCategoryId } from '../../core/categorize.ts'
import { api } from '../lib/api.ts'
import { Shell } from '../components/Shell.tsx'
import { Button } from '../components/ui/button.tsx'
import { Input, Label, Select } from '../components/ui/input.tsx'
import { Switch } from '../components/ui/switch.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { Badge } from '../components/ui/badge.tsx'
import { useToast } from '../components/ui/toast.tsx'
import { cn, plural } from '../lib/utils.ts'
import type { Theme } from '../lib/theme.ts'
import { useAdminMode } from '../lib/useAdminMode.tsx'
import { AdminNotice } from '../components/AdminNotice.tsx'

type Pick = { recommended: boolean; note: string | null }

/**
 * Kurátorská doporučení. Vědomě jiná věc než „dílo pomůže splnit chybějící
 * pravidlo": tohle je názor člověka o tom, co je dobrá volba, a platí pro celý
 * kánon — nastaví se jednou a vidí je každý, kdo si z něj skládá seznam.
 *
 * Stránka nemá vlastní zámek: kdo zná odkaz, může editovat. Je to stejná volba
 * jako u seznamů bez hesla a stejně vědomá.
 */
export function RecommendationsPage({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const [canon, setCanon] = useState<Canon | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [picks, setPicks] = useState<Record<string, Pick>>({})
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [onlyPicked, setOnlyPicked] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { locked } = useAdminMode()
  const toast = useToast()
  // Jen změny od uživatele mají spustit automatické ukládání — ne počáteční
  // načtení dat z API, které taky projde přes setPicks.
  const dirty = useRef(false)

  useEffect(() => {
    void (async () => {
      try {
        const canons = await api.canons()
        if (!canons[0]) return setError('Není žádný kánon — nejdřív ho naimportuj.')
        const [c, profiles] = await Promise.all([api.canon(canons[0].id), api.profiles()])
        setCanon(c)
        setProfile(profiles[0] ?? null)
        setPicks(Object.fromEntries(c.works.map((w) => [w.id, { recommended: w.recommended, note: w.recommendedNote }])))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Načtení se nepodařilo')
      }
    })()
  }, [])

  const pickedCount = useMemo(() => Object.values(picks).filter((p) => p.recommended).length, [picks])

  const visible = useMemo(() => {
    if (!canon || !profile) return []
    const q = query.trim().toLowerCase()
    return canon.works.filter((w) => {
      if (q && !`${w.author ?? ''} ${w.title}`.toLowerCase().includes(q)) return false
      if (category && deriveCategoryId(w, profile) !== category) return false
      if (onlyPicked && !picks[w.id]?.recommended) return false
      return true
    })
  }, [canon, profile, query, category, onlyPicked, picks])

  // Automatické ukládání: 1,2 s ticha od poslední změny, ať se needitovaný
  // řádek neukládá po každém stisku klávesy v poznámce. Hook musí běžet
  // před podmíněnými návraty níž, i když načtení ještě neskončilo.
  useEffect(() => {
    if (!dirty.current || !canon) return
    const t = setTimeout(() => {
      if (!busy) void save()
    }, 1200)
    return () => clearTimeout(t)
  }, [picks])

  if (error && !canon) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Doporučená díla"><Alert variant="destructive">{error}</Alert></Shell>
  }
  if (!canon || !profile) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Doporučená díla"><p className="text-[var(--muted-foreground)]">Načítám…</p></Shell>
  }

  const set = (id: string, patch: Partial<Pick>) => {
    dirty.current = true
    setPicks((prev) => ({ ...prev, [id]: { recommended: false, note: null, ...prev[id], ...patch } }))
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.setRecommendations(
        canon!.id,
        Object.entries(picks).map(([workId, p]) => ({ workId, recommended: p.recommended, note: p.note?.trim() || null })),
      )
      toast(`Uloženo — ${plural(res.recommended, 'doporučené dílo', 'doporučená díla', 'doporučených děl')}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  /** Export a import, aby se výběr dal poslat učitelce a zase přijmout zpátky. */
  function exportPicks() {
    const payload = {
      canonId: canon!.id,
      canonName: canon!.name,
      recommendations: canon!.works
        .filter((w) => picks[w.id]?.recommended)
        .map((w) => ({ author: w.author, title: w.title, note: picks[w.id]?.note ?? null })),
    }
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }))
    a.download = `doporucena-${canon!.id}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function importPicks(file: File) {
    setError(null)
    try {
      const data = JSON.parse(await file.text()) as { recommendations?: { author: string | null; title: string; note?: string | null }[] }
      const byTitle = new Map(canon!.works.map((w) => [`${w.author ?? ''}|${w.title}`.toLowerCase(), w.id]))
      const next: Record<string, Pick> = Object.fromEntries(canon!.works.map((w) => [w.id, { recommended: false, note: null }]))
      let matched = 0
      let missed = 0
      for (const r of data.recommendations ?? []) {
        const id = byTitle.get(`${r.author ?? ''}|${r.title}`.toLowerCase())
        if (!id) { missed++; continue }
        next[id] = { recommended: true, note: r.note ?? null }
        matched++
      }
      dirty.current = true
      setPicks(next)
      toast(`Načteno ${matched} doporučení${missed ? `, ${missed} se v tomhle kánonu nenašlo` : ''}.`)
    } catch {
      setError('Soubor se nepodařilo přečíst jako seznam doporučení.')
    }
  }

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title="Doporučená díla"
      subtitle="Označ díla, která stojí za výběr. Doporučení platí pro celý kánon a uvidí je každý, kdo si z něj skládá seznam."
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={exportPicks}><Download size={15} aria-hidden /> Export</Button>
          <Button variant="ghost" size="sm" asChild>
            <label className="cursor-pointer">
              <Upload size={15} aria-hidden /> Import
              <input type="file" accept="application/json" className="sr-only" onChange={(e) => e.target.files?.[0] && importPicks(e.target.files[0])} />
            </label>
          </Button>
        </>
      }
    >
      <AdminNotice what="Doporučená díla" />
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      {/* Uložit stojí hned nad tabulkou, ne schované v hlavičce — a ukládá
          se navíc samo, tohle je pro jistotu / okamžitou zpětnou vazbu. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--card)] px-3 py-2">
        <Badge>{pickedCount} vybráno</Badge>
        <div className="flex items-center gap-2">
          <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">Ukládá se automaticky</span>
          <Button size="sm" onClick={save} disabled={busy || locked}>{busy ? 'Ukládám…' : 'Uložit'}</Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Hledat autora nebo dílo…" aria-label="Hledat" className="w-full sm:w-64" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filtr kategorie">
          <option value="">Všechny kategorie</option>
          {[...profile.categories].sort((a, b) => a.order - b.order).map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </Select>
        <label className="ml-auto flex items-center gap-2 text-[length:var(--text-sm)]">
          <Switch checked={onlyPicked} onCheckedChange={setOnlyPicked} aria-label="Zobrazit jen doporučená" />
          Jen doporučená
        </label>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)]">
        <div className="max-h-[min(44rem,calc(100dvh-16rem))] overflow-auto">
          <table className="w-full min-w-[34rem] text-left">
            <caption className="sr-only">Kánon s možností označit doporučená díla</caption>
            <thead className="sticky top-0 z-10 bg-[var(--card)]">
              <tr className="border-b border-[var(--border)] text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                <th scope="col" className="w-20 px-4 py-2.5 font-semibold">Doporučit</th>
                <th scope="col" className="px-4 py-2.5 font-semibold">Autor · Dílo</th>
                <th scope="col" className="px-3 py-2.5 font-semibold">Poznámka pro studenty</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((w) => {
                const pick = picks[w.id] ?? { recommended: false, note: null }
                return (
                  <tr
                    key={w.id}
                    data-recommended={pick.recommended || undefined}
                    className={cn(
                      'border-b border-[var(--border)] last:border-0',
                      pick.recommended && 'bg-[var(--work-suggested-surface)] shadow-[inset_3px_0_0_var(--work-suggested)]',
                    )}
                  >
                    <td className="px-4 py-[var(--space-3)]">
                      <Switch
                        checked={pick.recommended}
                        onCheckedChange={(recommended) => set(w.id, { recommended })}
                        disabled={locked}
                        aria-label={`Doporučit dílo ${w.title}`}
                      />
                    </td>
                    <td className="px-4 py-[var(--space-3)]">
                      <div className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                        {w.author ?? 'bez autora'}
                      </div>
                      <div className="font-serif text-[length:var(--text-lg)] font-medium tracking-[var(--tracking-title)]">{w.title}</div>
                    </td>
                    <td className="px-3 py-[var(--space-3)]">
                      <Input
                        inputSize="sm"
                        value={pick.note ?? ''}
                        placeholder={pick.recommended ? 'Proč zrovna tohle? (nepovinné)' : ''}
                        disabled={locked || !pick.recommended}
                        maxLength={200}
                        aria-label={`Poznámka k dílu ${w.title}`}
                        onChange={(e) => set(w.id, { note: e.target.value || null })}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-2 text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
        Zobrazeno {plural(visible.length, 'dílo', 'díla', 'děl')} ze {canon.works.length}.
      </p>
    </Shell>
  )
}
